import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { faCalendarXmark } from '@fortawesome/pro-light-svg-icons/faCalendarXmark';
import { TypedMemo } from '../../common/types';
import { errorAlertMessage } from '../../common/views/ErrorAlert/errorAlertMessage';
import { ConfirmDialog } from '../../common/views/modals/confirmDialog/ConfirmDialog';
import { ConfirmDialogIconMark } from '../../common/views/modals/confirmDialog/ConfirmDialogMark';
import { useCancelCampaign } from './useCancelCampaign';

interface CancelCampaignModalProps {
  isOpen: boolean;
  closeModal: () => void;
  accountId: number;
  campaignId: number;
  campaignUuid?: string;
  smartLinkId: number;
}

/**
 * The confirmation in front of the cancel (design dialogs a1). A cancel cannot be undone — the
 * campaign never goes back to `scheduled` — so the artist says it twice.
 */
const CancelCampaignModalComponent: React.FC<CancelCampaignModalProps> = ({
  isOpen,
  closeModal,
  accountId,
  campaignId,
  campaignUuid,
  smartLinkId,
}) => {
  const intl = useIntl();
  const { cancel, isLoading, error, reset } = useCancelCampaign({
    accountId,
    campaignId,
    campaignUuid,
    smartLinkId,
  });

  const onClose = useCallback(() => {
    reset();
    closeModal();
  }, [closeModal, reset]);

  const onConfirm = useCallback(() => {
    cancel()
      .then((cancelled) => {
        if (!cancelled) {
          return;
        }
        onClose();
      })
      .catch(console.error);
  }, [cancel, onClose]);

  return (
    <ConfirmDialog
      isOpen={isOpen}
      tone="danger"
      mark={<ConfirmDialogIconMark tone="danger" icon={faCalendarXmark} />}
      title={intl.formatMessage({ id: 'app.sml.presave.cancel.title_v2', defaultMessage: 'Stop the pre-save?' })}
      description={intl.formatMessage({
        id: 'app.sml.presave.cancel.help_v2',
        defaultMessage:
          'Fans can no longer pre-save the song on this page, and on release day nothing goes to their libraries. The page stays online, your song is not changed, and the fans who signed up stay in your counts. You cannot undo this.',
      })}
      cancelLabel={intl.formatMessage({ id: 'app.sml.presave.cancel.keep_v2', defaultMessage: 'Keep the pre-save' })}
      confirmLabel={intl.formatMessage({
        id: 'app.sml.presave.cancel.confirm_v2',
        defaultMessage: 'Stop the pre-save',
      })}
      isBusy={isLoading}
      error={error === undefined ? undefined : errorAlertMessage(intl, error)}
      onConfirm={onConfirm}
      onCancel={onClose}
      testId="presave-cancel-dialog"
    />
  );
};

export const CancelCampaignModal = TypedMemo(CancelCampaignModalComponent);
